import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Globe2, Star, Zap, Trophy, X, HelpCircle } from 'lucide-react';

/**
 * TravelerGlossary — a compact, dismissible "what do these words mean?" card
 * for new travelers. Explains WorldVibe-specific vocabulary in one plain-
 * English line each so the Home page stops feeling like an alien language
 * on first visit.
 *
 * Persistence: dismissal is stored in localStorage under `wv_glossary_dismissed`
 * so returning users don't see it again. The four terms match what actually
 * appears on the Home page — Planets, Stars, XP, and Cosmic Rank.
 */
const TERMS = [
  {
    icon: Globe2,
    color: '#22D3EE',
    name: 'Planets',
    desc: 'Themed chat rooms. Join one to talk with people who share your interests — music, gaming, travel, anime, and more.',
  },
  {
    icon: Star,
    color: '#F59E0B',
    name: 'Stars ⭐',
    desc: 'Your spendable currency. Earn free stars from daily tasks and quests, or top up. Use them to send gifts, get VIP, and unlock perks.',
  },
  {
    icon: Zap,
    color: '#A78BFA',
    name: 'XP',
    desc: 'Experience points. Every chat, gift sent, planet joined, or quest completed adds XP. XP is how you level up your Cosmic Rank.',
  },
  {
    icon: Trophy,
    color: '#EC4899',
    name: 'Cosmic Rank',
    desc: '12 progression tiers from Explorer to Cosmic Legend. Higher rank = fancier badge, more pet slots, exclusive planets, and bragging rights.',
  },
];

export default function TravelerGlossary() {
  const [dismissed, setDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('wv_glossary_dismissed') === '1',
  );

  if (dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem('wv_glossary_dismissed', '1');
    setDismissed(true);
  };

  return (
    <Card className="bg-gradient-to-br from-[#161622] to-[#1a1030] border-fuchsia-500/20" data-testid="home-traveler-glossary">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 shrink-0 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center">
            <HelpCircle size={18} className="text-fuchsia-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display font-semibold text-white">New here? Quick vocab guide</div>
                <div className="text-xs text-white/55 mt-0.5">Four words you&apos;ll see everywhere on WorldVibe — in plain English.</div>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                aria-label="Dismiss glossary"
                data-testid="home-glossary-dismiss"
                className="shrink-0 -mt-1 -mr-1 w-7 h-7 rounded-full text-white/40 hover:text-white/80 hover:bg-white/5 flex items-center justify-center transition"
              >
                <X size={15} />
              </button>
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
              {TERMS.map(({ icon: Icon, color, name, desc }) => (
                <div key={name} className="flex items-start gap-2.5" data-testid={`glossary-term-${name.toLowerCase().replace(/[^a-z]/g, '')}`}>
                  <span
                    className="mt-0.5 w-6 h-6 shrink-0 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${color}22`, border: `1px solid ${color}44` }}
                  >
                    <Icon size={12} style={{ color }} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-white">{name}</div>
                    <div className="text-[11px] text-white/55 leading-snug">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
